import values from 'lodash/values'
import type { RouteDef } from '@ztechapps/common.contract'

/**
 * QuickScore Admin routes definition.
 * Settings (Organization → Users), Teachers and Schools are the current management screens; a
 * teacher's Credits tab and a school's Ledger tab are the app's only credits surfaces — there is
 * no standalone credits route.
 * Auth pages are wired against the `/api/auth/{basic,passwordless}/quickscore-admin` backend routes.
 */
export const routesDef = {
  // Landing screen for /app, and the first crumb of every breadcrumb trail.
  OverviewPage: {
    name: 'Overview',
    path: '/app/overview',
    exact: true,
    layout: 'main',
  },

  // Management
  SettingsPage: {
    name: 'Settings',
    path: '/app/settings',
    exact: true,
    layout: 'main',
  },
  TeachersPage: {
    name: 'Teachers',
    path: '/app/teachers',
    exact: true,
    layout: 'main',
  },
  TeacherDetailPage: {
    name: 'Teacher',
    path: '/app/teachers/detail/:_id',
    altPath: ({ _id }: { _id: string }) => `/app/teachers/detail/${encodeURIComponent(_id)}`,
    exact: true,
    layout: 'main',
  },
  SchoolsPage: {
    name: 'Schools',
    path: '/app/schools',
    exact: true,
    layout: 'main',
  },
  SchoolDetailPage: {
    name: 'School',
    path: '/app/schools/detail/:_id',
    altPath: ({ _id }: { _id: string }) => `/app/schools/detail/${encodeURIComponent(_id)}`,
    exact: true,
    layout: 'main',
  },
  BlogsPage: {
    name: 'Help Articles',
    path: '/app/settings/blogs',
    exact: true,
    layout: 'main',
  },
  BlogItemPage: {
    name: 'Help Article',
    path: '/app/settings/blogs/:_id',
    altPath: ({ _id }: { _id: string }) => `/app/settings/blogs/${encodeURIComponent(_id)}`,
    exact: true,
    layout: 'main',
  },
  BlogPreviewPage: {
    name: 'Blog Preview',
    path: '/blog-preview/:slug',
    altPath: ({ slug }: { slug: string }) => `/blog-preview/${encodeURIComponent(slug)}`,
    exact: true,
    layout: 'blank',
  },
  ConversationPage: {
    name: 'Conversation',
    path: '/app/conversation',
    altPath: () => '/app/conversation',
    exact: true,
    layout: 'main',
  },
  // Standalone full page — a distinct path from the ConversationPage's nested `:_id` off-canvas child,
  // per components/tables.md's two-route-def rule. `:_id` is the conversation's external id.
  ConversationItemPage: {
    name: 'Conversation',
    path: '/app/conversation/detail/:_id',
    altPath: ({ _id }: { _id: string }) => `/app/conversation/detail/${encodeURIComponent(_id)}`,
    exact: true,
    layout: 'main',
  },

  // Auth pages
  LoginPage: {
    name: 'Login',
    path: '/account/login',
    altPath: () => '/account/login',
    exact: true,
    layout: 'blank',
  },
  JoinPage: {
    name: 'Join',
    path: '/account/join',
    exact: true,
    layout: 'blank',
  },
  ChangePasswordPage: {
    name: 'Change Password',
    path: '/account/change-password/:username?/:code?',
    altPath: () => '/account/change-password',
    exact: true,
    layout: 'blank',
  },
  AuthSuccessPage: {
    name: 'Auth Success',
    path: '/auth/success/:auth',
    exact: true,
    layout: 'blank',
  },
  AuthFailedPage: {
    name: 'Auth Failed',
    path: '/auth/failed',
    exact: true,
    layout: 'blank',
  },
  LoadingPage: {
    name: 'Loading',
    path: '/',
    exact: true,
    layout: 'blank',
  },
} as const satisfies Record<string, RouteDef>

export const routesArray = values(routesDef)
